import { format } from 'date-fns';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppIcon,
  DATE_FORMATS,
  EnrichedCryptoAddress,
  RowLabelContent,
  useAppDialog,
} from 'common';
import { APP_ROUTES } from '~/routes';
import { AppStore, DataStore } from '~/store';
import { Error } from '~/components/common/error';
import { DeleteAddressEntryModal } from './delete-address-entry-modal';
import { CancelAddressEntryModal } from './cancel-address-entry-modal';
import { AccountSelector } from '~/components/workflows/account-selector';
import { CopyableText } from '~/components/copyable-text';
import { NotAllowed } from '~/components/workflows/shared/not-allowed';
import { getAccountType } from '~/utils/get-account-details';
import { AddCryptoAddressFlow } from '~/components/workflows/add-crypto-address';
import { AddCryptoAddressSteps } from '~/components/workflows/add-crypto-address/steps';
import { AppLoader } from '~/components/app-loader';
import { CurrencyIdentity } from '@xbto/universal-components';

interface SettingsAddressBookDetailsProps {
  error?: string | null;
  address: EnrichedCryptoAddress;
  onPressSend?: () => void;
  isLoading?: boolean;
}

export const SettingsAddressBookDetails: FC<
  SettingsAddressBookDetailsProps
> = ({ error, address, onPressSend, isLoading }) => {
  /**
   * Store
   */
  const accounts = DataStore.useStoreState(s => s.portfolio.accounts);
  const isKycVerified = DataStore.useStoreState(
    s => s.user.isKycVerified ?? false
  );

  const getAccountDetail = DataStore.useStoreActions(
    a => a.portfolio.getAccountDetail
  );
  const setAccountDetail = DataStore.useStoreActions(
    a => a.portfolio.setAccountDetail
  );
  const getWithdrawalAddress = DataStore.useStoreActions(
    a => a.settings.cryptoAddresses.getWithdrawalAddress
  );

  const { setDashboardSelectedDialogType, setDashboardSelectedAsset } =
    AppStore.useStoreActions(_ => ({
      setDashboardSelectedDialogType: _.setDashboardSelectedDialogType,
      setDashboardSelectedAsset: _.setDashboardSelectedAsset,
    }));

  /**
   * Hooks
   */
  const navigate = useNavigate();
  const { showDialog, hideDialog } = useAppDialog();

  /**
   * Methods
   */
  const beginSendWorkflow = async (accountId?: string) => {
    if (!accountId) {
      return;
    }
    const { isSuccessful, result } = await getAccountDetail({
      accountId,
      isBackgroundXHR: false,
    });
    if (!isSuccessful) {
      return;
    }
    const assetInAccount =
      result?.assets.find(a => a.currency.code === address.currencyCode) ||
      null;

    if (!assetInAccount) {
      showDialog(
        <NotAllowed
          message={`There is no ${
            address.currencyDisplayCode
          } in your account ${getAccountType(result?.account)} ${
            result?.account?.accountNumber
          } to perform this operation`}
          onClose={() => {
            setAccountDetail(null);
            hideDialog();
          }}
        />
      );
    } else {
      setDashboardSelectedAsset(assetInAccount);
      if (!isKycVerified) {
        setDashboardSelectedDialogType('KYC');
      }
      setDashboardSelectedDialogType('workflow-send');
    }
  };
  const handleSend = () => {
    onPressSend?.();

    if (accounts && accounts.length === 1) {
      beginSendWorkflow(accounts[0].account?.accountId);
      return;
    }

    showDialog(
      <AccountSelector
        workflowType={'workflow-send'}
        onClose={hideDialog}
        onNext={async accountItem => {
          hideDialog();
          beginSendWorkflow(accountItem.account?.accountId);
        }}
      />
    );
  };
  const handleCloseCancelDeleteWithdrawalAddress = () => {
    hideDialog();
    navigate(APP_ROUTES.AUTH_SETTINGS_CRYPTO_ADDRESSES);
  };
  const handleDelete = () => {
    showDialog(
      <DeleteAddressEntryModal addressId={address.id} onClose={hideDialog} />
    );
  };
  const handleVerify = () => {
    showDialog(
      <AddCryptoAddressFlow
        onClose={hideDialog}
        showIntro={false}
        startStep={AddCryptoAddressSteps.Code}
        refreshAddresses={() => {
          getWithdrawalAddress(address.id);
        }}
      />
    );
  };
  const handleCancel = () => {
    showDialog(
      <CancelAddressEntryModal
        addressId={address.id}
        onClose={handleCloseCancelDeleteWithdrawalAddress}
      />
    );
  };

  const isVerifying = address.isVerifying;
  /**
   * DOM
   */
  const showSendButton = !isVerifying; // Note: addresses are global to all accounts, so one can send to an address (contactlist) from any account
  return isLoading ? (
    <AppLoader spinnerTop="104px" />
  ) : (
    <div className="flex flex-col h-full">
      {/* heading  */}
      {isVerifying && (
        <div className="px-6 pt-6 pb-2">
          <div className="flex flex-col bg-yellow-100 px-6 py-4 text-primary text-center justify-center items-center font-bold rounded-md">
            Pending
          </div>
        </div>
      )}
      <div className="flex flex-col md:flex-row gap-y-2 px-4 sm:px-6 py-4 justify-between ">
        <div className="flex flex-row items-end gap-x-2">
          <CurrencyIdentity
            name={address.address}
            code={address.currencyCode}
            displayCode={address.currencyCode}
          />
          <CopyableText
            showCopyText={false}
            showText={false}
            text={address.address}
            small
          />
        </div>
        <div className="flex flex-col items-center md:flex-row gap-y-2 gap-x-1">
          {showSendButton && (
            <button className="app-button-outline text-sm" onClick={handleSend}>
              Send <AppIcon cls="ml-2" icon="send" bg="" size={16} />
            </button>
          )}
          {!isVerifying && (
            <button
              className="w-full md:w-auto app-button-failure text-sm"
              onClick={handleDelete}
            >
              Remove
            </button>
          )}
          {isVerifying && (
            <button
              className="w-full md:w-auto app-button-outline text-sm"
              onClick={handleVerify}
            >
              Verify
            </button>
          )}
          {isVerifying && (
            <button
              className="w-full md:w-auto app-button-failure text-sm"
              onClick={handleCancel}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <RowLabelContent
        cls="px-6"
        label={'Address label'}
        content={
          <span className={`text-right break-normal`}>{address.label}</span>
        }
      />

      <RowLabelContent
        cls="px-6"
        label={'Asset'}
        content={
          <CurrencyIdentity code={address.currencyCode} displayCode={null} />
        }
      />

      <RowLabelContent
        cls="px-6"
        label={'Network'}
        content={
          <span className={`text-right break-normal`}>{address.network}</span>
        }
      />
      <RowLabelContent
        cls="px-6"
        label={'Date added'}
        content={
          <span
            className={`text-right break-normal text-primary font-bold`}
          >{`${format(
            address.creationDate,
            DATE_FORMATS.DD_MONTH_YYYY
          )} - ${format(address.creationDate, DATE_FORMATS.HH_MM_TZ)}`}</span>
        }
      />

      {/* error */}
      {error && <Error cls="px-6 py-1 md:px-12" message={error} />}

      {/* content */}
    </div>
  );
};
